import React, { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user && typeof user === "object") {
    const to = location.state?.from || "/admin";
    return <Navigate to={to} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back, Srehan!");
      navigate(location.state?.from || "/admin", { replace: true });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Login failed";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="bg-zinc-100 min-h-[80vh] flex items-center justify-center px-4 py-12">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white border-4 border-black p-8 brutal-shadow-lg"
      >
        <div className="flex items-center gap-2 mb-6">
          <div className="bg-black text-white p-2">
            <Lock size={20} strokeWidth={3} />
          </div>
          <h1 className="font-anton uppercase text-4xl leading-none">Admin</h1>
        </div>

        <label className="block mb-4">
          <span className="font-anton uppercase text-sm tracking-widest mb-2 block">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email-input"
            className="brutal-input"
            required
            autoComplete="email"
          />
        </label>

        <label className="block mb-6">
          <span className="font-anton uppercase text-sm tracking-widest mb-2 block">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="brutal-input"
            required
            autoComplete="current-password"
          />
        </label>

        {error && (
          <div data-testid="admin-login-error" className="mb-4 border-2 border-[#FF3B30] bg-red-50 p-3 text-sm font-outfit text-[#FF3B30]">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="admin-login-submit"
          className="brutal-btn w-full justify-center disabled:opacity-60"
        >
          {submitting ? "Signing in…" : "Sign In"}
        </button>
      </motion.form>
    </div>
  );
}
